// client/src/components/secure/territory/Shared.js — pieces shared by the Territories,
// Lead Routing, My Plan and Task Automation screens.
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';

export const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString() : '—');
export const fmtDateTime = (d) => (d ? new Date(String(d).replace(' ', 'T')).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');
export const VISIT_LABELS = {
    showing: 'Showing', listing_appointment: 'Listing appointment', open_house: 'Open house',
    door_knocking: 'Door-knocking', farming: 'Farming', property_preview: 'Property preview', other: 'Other',
};
export const CLASSES = ['residential', 'commercial', 'industrial', 'rental'];

export const Card = ({ title, children, right, className = '' }) => (
    <div className={`bg-white rounded-lg shadow p-4 md:p-6 ${className}`}>
        {(title || right) && (
            <div className="flex flex-wrap gap-2 justify-between items-center mb-3">
                {title && <h2 className="text-lg font-semibold text-slate-800">{title}</h2>}
                {right}
            </div>
        )}
        {children}
    </div>
);
export const Empty = ({ children }) => <p className="text-sm text-slate-500">{children}</p>;
export const ErrorLine = ({ error }) => (error ? <p className="text-sm text-red-600 my-2">{error}</p> : null);
export const Tabs = ({ tabs, value, onChange }) => (
    <div className="flex flex-wrap gap-1 border-b mb-4">
        {tabs.map(([k, label]) => (
            <button key={k} type="button" onClick={() => onChange(k)}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${value === k ? 'border-slate-700 text-indigo-700 font-medium' : 'border-transparent text-slate-600 hover:text-slate-800'}`}>
                {label}
            </button>
        ))}
    </div>
);
export const Btn = ({ children, kind = 'primary', ...p }) => (
    <button type="button" {...p}
        className={`px-3 py-1.5 text-sm rounded-md disabled:opacity-50 ${kind === 'primary' ? 'bg-slate-800 text-white hover:bg-slate-900'
            : kind === 'danger' ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'} ${p.className || ''}`}>
        {children}
    </button>
);
export const input = 'p-2 border rounded text-sm w-full';

/**
 * Documents attached to a territory, visit, plan, routed lead, task or template.
 * Files go into the firm's document repository (the existing store); links point at
 * documents already filed elsewhere.
 */
export const DocLinks = ({ entityType, entityId, canEdit = true }) => {
    const api = useApiClient();
    const [links, setLinks] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [file, setFile] = useState(null);
    const [link, setLink] = useState({ doc_source: 'repository', doc_id: '', label: '' });

    const load = useCallback(async () => {
        try { setLinks(await api.get(`/api/territory/documents?entity_type=${entityType}&entity_id=${entityId}`)); setError(''); }
        catch (e) { setError(e.message); }
    }, [api, entityType, entityId]);
    useEffect(() => { load(); }, [load]);

    const upload = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('document', file);
            fd.append('entity_type', entityType);
            fd.append('entity_id', entityId);
            await api.post('/api/territory/documents/upload', fd, true);
            setFile(null); load();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const attach = async () => {
        if (!link.doc_id) return;
        setBusy(true);
        try { await api.post('/api/territory/documents/link', { ...link, entity_type: entityType, entity_id: entityId }); setLink({ ...link, doc_id: '', label: '' }); load(); }
        catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    // Every target (repository download, cabinet, forms library, /uploads) answers an
    // authenticated GET; fetch it with the token and open the blob.
    const open = async (l) => {
        if (!l.open_url) return;
        try {
            const r = await fetch(l.open_url, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
            if (!r.ok) throw new Error(`Could not open the document (${r.status}).`);
            window.open(URL.createObjectURL(await r.blob()), '_blank', 'noopener');
        } catch (e) { setError(e.message); }
    };
    const detach = async (l) => {
        if (!window.confirm('Detach this document? The document itself stays filed.')) return;
        try { await api.delete(`/api/territory/documents/${l.link_id}`); load(); } catch (e) { setError(e.message); }
    };

    return (
        <div className="text-sm">
            <ErrorLine error={error} />
            {links.length === 0 ? <Empty>No documents attached.</Empty> : (
                <ul className="divide-y mb-2">
                    {links.map((l) => (
                        <li key={l.link_id} className="py-1 flex justify-between gap-2">
                            {l.can_open
                                ? <button type="button" className="text-indigo-700 hover:underline text-left" onClick={() => open(l)}>{l.title}</button>
                                : <span className="text-slate-500" title="You cannot open this document">{l.title}</span>}
                            <span className="text-xs text-slate-400 whitespace-nowrap">{l.doc_source.replace('_', ' ')}
                                {canEdit && <button type="button" className="ml-2 text-red-600" onClick={() => detach(l)}>detach</button>}</span>
                        </li>
                    ))}
                </ul>
            )}
            {canEdit && (
                <div className="flex flex-wrap gap-2 items-center">
                    <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs" />
                    <Btn onClick={upload} disabled={!file || busy}>Upload &amp; attach</Btn>
                    <span className="text-xs text-slate-400">or link existing</span>
                    <select value={link.doc_source} onChange={(e) => setLink({ ...link, doc_source: e.target.value })} className="p-1 border rounded text-xs">
                        <option value="repository">Document repository #</option>
                        <option value="client_document">Client document #</option>
                        <option value="property_document">Property document #</option>
                        <option value="compliance_document">Deal compliance document #</option>
                        <option value="forms_library">Forms library #</option>
                        <option value="cabinet">My file cabinet #</option>
                    </select>
                    <input value={link.doc_id} onChange={(e) => setLink({ ...link, doc_id: e.target.value.replace(/\D/g, '') })} placeholder="id" className="p-1 border rounded text-xs w-16" />
                    <input value={link.label} onChange={(e) => setLink({ ...link, label: e.target.value })} placeholder="label (optional)" className="p-1 border rounded text-xs w-32" />
                    <Btn kind="secondary" onClick={attach} disabled={!link.doc_id || busy}>Link</Btn>
                </div>
            )}
        </div>
    );
};
